import { Card, CardContent } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { Printer } from 'lucide-react';
import { useWizardStore } from '../wizard-store';

// KGC kanonikus bérbeadó-adat (kgc_business_facts) — fix cégadat, NEM fordítható.
// TODO(Phase-2): bérbeadó-adat tenant-settings-ből (Tenant.settings), ne LESSOR_* konstans.
// Phase-1 single-tenant KGC-döntés (Javo-ratify 2026-06-04); multi-tenant onset-kor cserélendő,
// különben minden tenant KGC-ként jelenne meg (tenant-leak smell).
const LESSOR_NAME = 'Kisgépcentrum Kft.';
const LESSOR_TAX_NUMBER = '12339657-2-13';

/**
 * v3-parity step-4: strukturált szerződés-előnézet kártya (b-variáns).
 * A pénzügy/időszak/gép-lista a SummaryStep többi kártyájában renderel — ez
 * az ÚJ szerződés-fejléc + BÉRBEADÓ/BÉRLŐ felek-blokk + PDF-CTA réteget adja.
 * Tiszta FE a wizard-store-ból; draft-állapotban nincs valódi szerződésszám,
 * a PDF-CTA a véglegesítés után aktiválódik (a contract-PDF a /contracts/:id-n él).
 */
export function ContractPreviewCard() {
  const { t } = useTranslation();
  const { partner, draftId } = useWizardStore();

  // Draft-ref: a draftId rövid, ember-olvasható szelete (valódi szerződésszám
  // csak a rental/contract create után keletkezik a BE-n).
  const draftRef = (draftId ?? '').slice(0, 8).toUpperCase();
  const renterLocation = [partner?.city, partner?.address].filter(Boolean).join(' · ');

  return (
    <Card data-testid="contract-preview-card">
      <CardContent className="p-0">
        {/* Fejléc: cím + draft-szerződésszám | bérbeadó + PDF-CTA */}
        <div className="flex items-start justify-between gap-4 border-b-2 border-gray-900 px-8 py-5 dark:border-white">
          <div>
            <h4 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              {t('rental.contractCard.title')}
            </h4>
            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500 dark:text-[#ccfbf1]">
              <span className="font-mono font-semibold text-gray-900 dark:text-white">
                {t('rental.wizard.contractNumber', { number: draftRef })}
              </span>
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                {t('rental.wizard.draftRef')}
              </span>
            </p>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-gray-900 dark:text-white">{LESSOR_NAME}</div>
            <div className="text-[11px] text-gray-500 dark:text-[#ccfbf1]">
              {t('partner.taxNumber')}: {LESSOR_TAX_NUMBER}
            </div>
            <button
              type="button"
              disabled
              title={t('rental.contractCard.pdfAfterFinalize')}
              className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Printer className="h-3.5 w-3.5" />
              {t('rental.contractCard.pdfPreview')}
            </button>
          </div>
        </div>

        {/* Felek-blokk: BÉRBEADÓ / BÉRLŐ */}
        <div className="grid grid-cols-1 gap-6 px-8 py-4 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-[#ccfbf1]">
              {t('rental.contractCard.lessor')}
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">{LESSOR_NAME}</div>
            <div className="text-xs text-gray-500 dark:text-[#ccfbf1]">
              {t('partner.taxNumber')}: {LESSOR_TAX_NUMBER}
            </div>
          </div>
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-[#ccfbf1]">
              {t('rental.contractCard.renter')}
            </div>
            <div className="text-sm font-bold text-gray-900 dark:text-white">
              {partner?.name ?? '—'}
            </div>
            {partner?.maskedIdCardNumber && (
              <div className="text-xs text-gray-500 dark:text-[#ccfbf1]">
                {partner.maskedIdCardNumber}
              </div>
            )}
            {renterLocation && (
              <div className="text-xs text-gray-500 dark:text-[#ccfbf1]">{renterLocation}</div>
            )}
            {partner?.phone && (
              <div className="text-xs text-gray-500 dark:text-[#ccfbf1]">{partner.phone}</div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
